import { useEffect, useState } from 'react';
import { siteApi } from '@/api/endpoints';
import type { NavItemKey } from '@/api/types';
import { useInitialData } from '@/ssr/initialData';
import { isHydrating } from '@/ssr/hydration';

export const NAV_LINKS = [
  { id: 'courses', to: '/courses', key: 'nav.courses' },
  { id: 'learn', to: '/learn', key: 'nav.learn' },
  { id: 'problems', to: '/problems', key: 'nav.problems' },
  { id: 'contests', to: '/contests', key: 'nav.contests' },
  { id: 'premium', to: '/pricing', key: 'nav.premium' },
  { id: 'about', to: '/about', key: 'nav.about' },
] as const satisfies ReadonlyArray<{ id: NavItemKey; to: string; key: string }>;

export type NavLinkItem = (typeof NAV_LINKS)[number];

const STORAGE_KEY = 'sqlm-nav-hidden';

/** Sessiya davomida bitta so'rov; layout almashganda (Site ↔ App) qayta "sakramasin" */
let cached: NavItemKey[] | null = null;
let request: Promise<NavItemKey[]> | null = null;

function readStored(): NavItemKey[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as NavItemKey[]) : null;
  } catch {
    return null;
  }
}

function loadHidden(): Promise<NavItemKey[]> {
  request ??= siteApi.nav().then((res) => {
    const hidden = res.data.filter((i) => !i.visible).map((i) => i.key);
    cached = hidden;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(hidden));
    } catch {
      /* storage yo'q — keyingi safar ham API dan */
    }
    return hidden;
  });
  return request;
}

/**
 * Admin yashirmagan menyu bandlari (Navbar, MobileMenu, Footer).
 * Hydration renderi faqat prerender ma'lumotidan (server HTML bilan bir xil bo'lsin);
 * SPA shell sahifalarda — keshdan, keyin API dan yangilanadi.
 */
export function useNavLinks(): NavLinkItem[] {
  const initial = useInitialData();
  const [hidden, setHidden] = useState<readonly NavItemKey[]>(() =>
    isHydrating() ? (initial?.navHidden ?? []) : (cached ?? readStored() ?? []),
  );

  useEffect(() => {
    let cancelled = false;
    loadHidden()
      .then((h) => {
        if (!cancelled) setHidden(h);
      })
      .catch(() => {
        request = null; // keyingi mount'da qayta urinadi; hozirgi holat qoladi
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return NAV_LINKS.filter((l) => !hidden.includes(l.id));
}
